import React, { useState, useMemo, useRef, useEffect } from "react";
import { formatDateTime } from "../../../utils/datetime";

/**
 * Chat Component
 * 
 * A reusable chat interface component.
 * Displays chat messages as bubbles with user name and timestamp.
 * Allows adding new messages (no edit/delete).
 * 
 * @param {Array} messages - Array of message objects with {user_first, user_last, message, timestamp}
 * @param {Function} onAddMessage - Callback function when a new message is added (receives message text)
 * @param {boolean} disabled - Whether the input form should be disabled
 * @param {string} title - Title for the chat section (default: "Comments")
 * @param {string} placeholder - Placeholder text for the input (default: "Add a comment...")
 * @param {string} emptyMessage - Message to show when there are no messages (default: "No comments yet. Be the first to add one!")
 */
const Chat = ({ 
  messages = [], 
  onAddMessage, 
  disabled = false,
  title = "Comments",
  placeholder = "Add a comment...",
  emptyMessage = "No comments yet. Be the first to add one!"
}) => {
  const [newMessage, setNewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesContainerRef = useRef(null);

  // Generate a muted color for a user based on their ID
  const generateMutedColor = (userId) => {
    // Use a simple hash function to get consistent colors per user
    const hash = userId ? String(userId).split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0) : 0;
    
    // Generate muted colors with low saturation (20-40%) and medium lightness (60-80%)
    const hue = Math.abs(hash) % 360;
    const saturation = 25 + (Math.abs(hash) % 15); // 25-40%
    const lightness = 70 + (Math.abs(hash) % 10); // 70-80%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Process messages to assign colors and alignments per user
  const processedMessages = useMemo(() => {
    const userColors = new Map();
    const userAlignments = new Map();
    let alignmentCounter = 0; // Track which alignment to assign next

    return messages.map((msg) => {
      const userId = msg.user_id || `${msg.user_first}_${msg.user_last}`;
      
      // Assign color if not already assigned
      if (!userColors.has(userId)) {
        userColors.set(userId, generateMutedColor(userId));
      }
      
      // Assign alignment if not already assigned (alternate per user)
      if (!userAlignments.has(userId)) {
        userAlignments.set(userId, alignmentCounter % 2 === 0 ? 'left' : 'right');
        alignmentCounter++;
      }
      
      return {
        ...msg,
        userColor: userColors.get(userId),
        alignment: userAlignments.get(userId),
      };
    });
  }, [messages]);

  // Auto-scroll to bottom when new messages are added (only within chat container)
  useEffect(() => {
    if (messagesContainerRef.current && processedMessages.length > 0) {
      messagesContainerRef.current.scrollTo({ 
        top: messagesContainerRef.current.scrollHeight, 
        behavior: "smooth" 
      });
    }
  }, [processedMessages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newMessage.trim() || isSubmitting || disabled) return;

    setIsSubmitting(true);
    try {
      await onAddMessage(newMessage.trim());
      setNewMessage("");
    } catch (error) {
      console.error("Failed to add message:", error);
      // Don't let errors cause page reload
    } finally {
      setIsSubmitting(false);
    }
    return false; // Additional safeguard
  };

  return (
    <div className="border-b pb-4">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">{title}</h2>
      
      {/* Chat Messages */}
      <div 
        ref={messagesContainerRef}
        className="mb-4 max-h-96 overflow-y-auto space-y-3 pr-2"
      >
        {processedMessages && processedMessages.length > 0 ? (
          processedMessages.map((msg, index) => {
            const isLeftAligned = msg.alignment === 'left';
            return (
              <div 
                key={index} 
                className={`flex flex-col ${isLeftAligned ? 'items-start' : 'items-end'}`}
              >
                <div 
                  className="inline-block border rounded-lg p-3 max-w-2xl"
                  style={{
                    backgroundColor: msg.userColor,
                    borderColor: msg.userColor,
                  }}
                >
                  <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {msg.message}
                  </p>
                </div>
                <div className={`mt-1 ${isLeftAligned ? 'ml-1' : 'mr-1'}`}>
                  <span className="text-xs font-medium text-gray-700">
                    {msg.user_first} {msg.user_last}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {msg.timestamp ? formatDateTime(msg.timestamp) : ""}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-sm text-gray-400 italic py-0">
            {emptyMessage}
          </div>
        )}
      </div>

      {/* Add Message Form */}
      {!disabled && (
        <form onSubmit={handleSubmit} className="space-y-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={3}
            placeholder={placeholder}
            className="w-full px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            disabled={isSubmitting}
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }}
              disabled={!newMessage.trim() || isSubmitting}
              className="px-4 py-1.5 bg-primary text-white hover:bg-secondary-600 hover:text-gray-100 rounded-md font-bold cursor-pointer transition-all ease-in-out duration-300 disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50"
            >
              {isSubmitting ? "Sending..." : "Send"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Chat;
